import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  User, Building2, Phone, Mail, MapPin, Calendar, 
  FileText, Upload, CheckCircle, AlertTriangle, 
  Shield, Briefcase, ChevronLeft, Save
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContractorProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [complianceDocs, setComplianceDocs] = useState([]);
  const [jobStats, setJobStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadType, setUploadType] = useState('Public Liability Insurance');
  const [uploadExpiry, setUploadExpiry] = useState('');

  useEffect(() => {
    if (id) {
      loadProfile();
    }
  }, [id]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/contractors/${id}/full-profile`);
      setProfile(res.data.contractor);
      setComplianceDocs(res.data.compliance_documents || []);
      setJobStats(res.data.job_stats);
      setFormData(res.data.contractor || {});
    } catch (err) {
      toast.error('Failed to load contractor profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API}/api/contractors/${id}/compliance`, {
        company_name: formData.company_name,
        contact_name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        trade_type: formData.trade_type,
        regions_covered: formData.regions_covered,
        emergency_availability: formData.emergency_availability,
        insurance_expiry: formData.insurance_expiry,
        gas_safe_number: formData.gas_safe_number,
        gas_safe_expiry: formData.gas_safe_expiry,
        niceic_number: formData.niceic_number,
        niceic_expiry: formData.niceic_expiry,
        accreditations: formData.accreditations,
        payment_terms: formData.payment_terms,
        notes: formData.notes,
      });
      toast.success('Profile updated successfully');
      loadProfile();
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', uploadFile);
      formDataUpload.append('document_type', uploadType);
      if (uploadExpiry) formDataUpload.append('expiry_date', uploadExpiry);
      
      await axios.post(`${API}/api/contractors/${id}/upload-compliance-doc`, formDataUpload);
      toast.success('Document uploaded');
      setUploadFile(null);
      setUploadExpiry('');
      loadProfile();
    } catch (err) {
      toast.error('Failed to upload document');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const isExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    try {
      const date = new Date(dateStr);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
      return date <= thirtyDaysFromNow;
    } catch {
      return false;
    }
  };

  const docTypes = [
    'Public Liability Insurance',
    'Gas Safe Certificate',
    'NICEIC Certificate',
    'Electrical Safety Certificate',
    'RAMS',
    'Method Statement',
    'Company Insurance',
    'Other Accreditation'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F7A78]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contractor-profile-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/contractors')} className="gap-2">
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{profile?.company_name || profile?.name}</h1>
          <p className="text-slate-500">{profile?.trade_type || 'General Contractor'}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#1F7A78] hover:bg-[#005456] gap-2">
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-blue-100">
                <Briefcase className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{jobStats?.total_jobs || 0}</p>
                <p className="text-sm text-slate-500">Total Jobs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-green-100">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{jobStats?.completed_jobs || 0}</p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-100">
                <Shield className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{jobStats?.completion_rate || 0}%</p>
                <p className="text-sm text-slate-500">Completion Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {/* Contact Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Company Name</Label>
                <Input
                  value={formData.company_name || ''}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Contact Name</Label>
                <Input
                  value={formData.contact_name || ''}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Trade Type</Label>
                <Input
                  value={formData.trade_type || ''}
                  onChange={(e) => setFormData({ ...formData, trade_type: e.target.value })}
                  placeholder="e.g. General, Plumbing, Electrical"
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Regions Covered</Label>
                <Input
                  value={formData.regions_covered?.join(', ') || ''}
                  onChange={(e) => setFormData({ ...formData, regions_covered: e.target.value.split(',').map(r => r.trim()) })}
                  placeholder="e.g. Essex, Kent, London"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Label>Emergency Availability</Label>
              <Button
                variant={formData.emergency_availability ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFormData({ ...formData, emergency_availability: !formData.emergency_availability })}
                className={formData.emergency_availability ? 'bg-green-600' : ''}
              >
                {formData.emergency_availability ? 'Yes' : 'No'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Certifications */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Certifications & Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Insurance Expiry</Label>
                <Input
                  type="date"
                  value={formData.insurance_expiry || ''}
                  onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })}
                  className="rounded-xl"
                />
                {isExpiringSoon(formData.insurance_expiry) && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Expiring soon
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Gas Safe Number</Label>
                <Input
                  value={formData.gas_safe_number || ''}
                  onChange={(e) => setFormData({ ...formData, gas_safe_number: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Gas Safe Expiry</Label>
                <Input
                  type="date"
                  value={formData.gas_safe_expiry || ''}
                  onChange={(e) => setFormData({ ...formData, gas_safe_expiry: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>NICEIC Number</Label>
                <Input
                  value={formData.niceic_number || ''}
                  onChange={(e) => setFormData({ ...formData, niceic_number: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>NICEIC Expiry</Label>
                <Input
                  type="date"
                  value={formData.niceic_expiry || ''}
                  onChange={(e) => setFormData({ ...formData, niceic_expiry: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Input
                  value={formData.payment_terms || ''}
                  onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                  placeholder="e.g. 30 days"
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="rounded-xl"
              />
            </div>
          </CardContent>
        </Card>

        {/* Document Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Compliance Document
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Document Type</Label>
                <select
                  value={uploadType}
                  onChange={(e) => setUploadType(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                >
                  {docTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Expiry Date (if applicable)</Label>
                <Input
                  type="date"
                  value={uploadExpiry}
                  onChange={(e) => setUploadExpiry(e.target.value)}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>File</Label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0])}
                className="rounded-xl"
              />
            </div>
            <Button
              onClick={handleUpload}
              disabled={!uploadFile}
              className="w-full bg-[#1F7A78] hover:bg-[#005456]"
            >
              Upload Document
            </Button>
          </CardContent>
        </Card>

        {/* Uploaded Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Compliance Documents ({complianceDocs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {complianceDocs.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">No documents uploaded</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-auto">
                {complianceDocs.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50">
                    <div>
                      <p className="font-medium text-sm">{doc.file_name}</p>
                      <p className="text-xs text-slate-500">
                        {doc.document_type}
                        {doc.expiry_date && (
                          <span className={isExpiringSoon(doc.expiry_date) ? 'text-amber-600 ml-2' : 'ml-2'}>
                            • Expires: {formatDate(doc.expiry_date)}
                          </span>
                        )}
                      </p>
                    </div>
                    <a
                      href={`${API}${doc.file_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#1F7A78] text-sm font-medium hover:underline"
                    >
                      View
                    </a>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
